import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Plus, Play, Square, Trash2, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, FilterTabs } from "./engShared";
import { appConfirm } from "@/components/AppDialogHost";

const POLL_STATUS = {
  draft: ["Draft", "text-ink-muted border-line bg-bg-elevated"],
  active: ["Live", "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"],
  closed: ["Closed", "text-ink-muted border-line bg-bg-elevated"],
};

export default function EngPolls() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/engagement/polls").then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try { await api.post(`/engagement/polls/${id}/action`, { action }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const create = async (e) => {
    e.preventDefault();
    setBusy("create");
    try {
      await api.post("/engagement/polls", { question: q, options: opts.filter(Boolean) });
      toast.success("Poll created — activate it to go live");
      setQ(""); setOpts(["", ""]); setShowNew(false); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Create failed"); }
    setBusy("");
  };
  const aiSuggest = async () => {
    setBusy("ai");
    try {
      const r = await api.post("/engagement/polls/ai-suggest", { topic: q });
      setQ(r.data.question || ""); setOpts(r.data.options?.length ? r.data.options : ["", ""]);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggest failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="eng-polls-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Polls</h1>
          <p className="text-sm text-ink-secondary">Ask your audience — live polls appear on your public listener page.</p>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="eng-poll-new-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-2 text-xs font-bold">
          <Plus className="h-3.5 w-3.5" /> New Poll
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="rounded-xl border border-line bg-bg-surface p-4 space-y-3" data-testid="eng-poll-form">
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Poll question (or a topic for AI to run with)…" data-testid="eng-poll-question"
              className="flex-1 rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" maxLength={300} />
            <button type="button" onClick={aiSuggest} disabled={!!busy} data-testid="eng-poll-ai-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-brand-amber/50 text-brand-amber px-3 py-1.5 text-xs font-bold">
              {busy === "ai" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI suggest
            </button>
          </div>
          {opts.map((o, i) => (
            <input key={i} value={o} onChange={(e) => setOpts((p) => p.map((x, j) => (j === i ? e.target.value : x)))}
              placeholder={`Option ${i + 1}`} data-testid={`eng-poll-option-${i}`} maxLength={120}
              className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-sm" />
          ))}
          <div className="flex gap-2">
            {opts.length < 6 && <button type="button" onClick={() => setOpts((p) => [...p, ""])} data-testid="eng-poll-add-option"
              className="rounded-full border border-line px-3 py-1.5 text-xs">+ option</button>}
            <button type="submit" disabled={busy === "create"} data-testid="eng-poll-create-btn"
              className="rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-bold">Create poll</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {!items.length && <div className="rounded-xl border border-line bg-bg-surface p-8 text-center text-sm text-ink-muted" data-testid="eng-polls-empty">No polls yet — create your first one.</div>}
        {items.map((p) => (
          <div key={p.poll_id} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`eng-poll-${p.poll_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <BarChart3 className="h-4 w-4 text-brand-cyan" />
              <span className="text-sm font-semibold flex-1">{p.question}</span>
              <Badge meta={POLL_STATUS[p.status]} />
              <span className="text-[11px] font-mono text-ink-muted">{p.total_votes || 0} votes</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {(p.options || []).map((o) => {
                const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
                return (
                  <div key={o.key} className="relative rounded-md border border-line/60 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-brand-cyan/15" style={{ width: `${pct}%` }} />
                    <div className="relative flex justify-between px-3 py-1.5 text-xs">
                      <span>{o.label}</span><span className="font-mono text-ink-muted">{o.votes} · {pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1.5 mt-3">
              {p.status !== "active" && <button onClick={() => act(p.poll_id, "activate")} data-testid={`eng-poll-activate-${p.poll_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-300 px-3 py-1 text-[11px]"><Play className="h-3 w-3" /> Go live</button>}
              {p.status === "active" && <button onClick={() => act(p.poll_id, "close")} data-testid={`eng-poll-close-${p.poll_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 text-[11px]"><Square className="h-3 w-3" /> Close</button>}
              <button onClick={async () => { if (await appConfirm("Delete this poll?")) act(p.poll_id, "delete"); }} data-testid={`eng-poll-delete-${p.poll_id}`}
                className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 text-rose-300 px-3 py-1 text-[11px]"><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
